import React, { useState } from "react";
import {
  Box,
  MenuItem,
  Select,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { ChevronRight, SlidersHorizontal, Building2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import SidebarMenuCardCst from "@components/SidebarMenuCardCst";
import { SidebarProps, SidebarSimpleItemProps } from "./types";
import {
  SIDEBAR_WIDTH,
  SIDEBAR_WIDTH_COLLAPSED,
  SidebarContainer,
  SidebarHeader,
  SidebarBody,
  SidebarSectionWrapper,
  SidebarSectionLabel,
  SimpleItemWrapper,
  SimpleItemContainer,
  SimpleItemContent,
  SimpleItemIconWrapper,
  SimpleItemChildrenContainer,
  SimpleItemChild,
  CollapsedIconTile,
} from "./style";

const SidebarSimpleItem: React.FC<SidebarSimpleItemProps> = ({
  icon,
  title,
  description,
  to,
  onClick,
  hasChevron = true,
  children,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const hasChildren = children && children.length > 0;

  const handleClick = () => {
    if (hasChildren) {
      setExpanded((prev) => !prev);
      return;
    }
    if (onClick) {
      onClick();
    } else if (to) {
      navigate(to);
    }
  };

  const handleChildClick = (child: { to?: string; onClick?: () => void }) => {
    if (child.onClick) {
      child.onClick();
    } else if (child.to) {
      navigate(child.to);
    }
  };

  return (
    <SimpleItemWrapper>
      <SimpleItemContainer onClick={handleClick}>
        {icon && <SimpleItemIconWrapper>{icon}</SimpleItemIconWrapper>}
        <SimpleItemContent>
          <Typography
            sx={{
              fontSize: "13px",
              fontWeight: 500,
              color: theme.palette.text.primary,
              lineHeight: 1.3,
            }}
          >
            {title}
          </Typography>
          {description && (
            <Typography
              sx={{
                fontSize: "11px",
                color: theme.palette.text.description,
                lineHeight: 1.3,
                marginTop: "2px",
              }}
            >
              {description}
            </Typography>
          )}
        </SimpleItemContent>
        {hasChevron && (
          <ChevronRight
            size={16}
            color={theme.palette.text.tertiary}
            style={{
              flexShrink: 0,
              transition: "transform 0.2s ease",
              transform: expanded ? "rotate(90deg)" : "rotate(0deg)",
            }}
          />
        )}
      </SimpleItemContainer>
      {hasChildren && (
        <SimpleItemChildrenContainer
          sx={{
            maxHeight: expanded ? `${children.length * 36}px` : "0px",
            opacity: expanded ? 1 : 0,
          }}
        >
          {children.map((child, index) => (
            <SimpleItemChild
              key={index}
              onClick={() => handleChildClick(child)}
            >
              <Typography
                sx={{
                  fontSize: "12px",
                  color: theme.palette.text.primary,
                  lineHeight: 1.4,
                }}
              >
                {child.title}
              </Typography>
            </SimpleItemChild>
          ))}
        </SimpleItemChildrenContainer>
      )}
    </SimpleItemWrapper>
  );
};

interface CollapsedItem {
  key: string;
  title: string;
  icon: React.ReactNode;
  active?: boolean;
  to?: string;
  onClick?: () => void;
  hasChildren: boolean;
}

const SidebarCst: React.FC<SidebarProps> = ({
  header,
  sections,
  width,
  collapsed = false,
  onExpandRequest,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const resolvedWidth =
    width ?? (collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH);

  if (collapsed) {
    const handleItemClick = (item: CollapsedItem) => {
      if (item.hasChildren) {
        onExpandRequest?.();
        return;
      }
      if (item.onClick) {
        item.onClick();
        return;
      }
      if (item.to) {
        navigate(item.to);
      }
    };

    return (
      <SidebarContainer width={resolvedWidth}>
        <SidebarHeader sx={{ alignItems: "center", padding: "20px 8px 16px" }}>
          <Box sx={{ textAlign: "center" }}>
            <Typography
              sx={{
                fontSize: "9px",
                fontWeight: 600,
                color: theme.palette.text.tertiary,
                textTransform: "uppercase",
                letterSpacing: "1px",
                marginBottom: "4px",
              }}
            >
              Corporate
            </Typography>
            <Typography
              sx={{
                fontSize: "13px",
                fontWeight: 700,
                color: theme.palette.primary.main,
                wordBreak: "break-word",
                lineHeight: 1.2,
              }}
            >
              {header.corporateName}
            </Typography>
          </Box>

          {header.initiativeOptions && header.initiativeOptions.length > 0 && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: "9px",
                  fontWeight: 600,
                  color: theme.palette.text.tertiary,
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                  marginBottom: "6px",
                }}
              >
                {header.initiativeLabel || "Iniziativa"}
              </Typography>
              <Tooltip
                title={
                  header.initiativeOptions.find(
                    (o) => o.value === header.selectedInitiative,
                  )?.label || ""
                }
                placement="right"
              >
                <CollapsedIconTile onClick={() => onExpandRequest?.()}>
                  <Building2 size={20} />
                </CollapsedIconTile>
              </Tooltip>
            </Box>
          )}
        </SidebarHeader>

        <SidebarBody sx={{ padding: "0 8px 16px", alignItems: "center" }}>
          {sections.map((section, sectionIndex) => {
            const items: CollapsedItem[] = [
              ...(section.type === "cards"
                ? section.items.map((item, i) => ({
                    key: `card-${i}`,
                    title: item.title,
                    icon: item.icon,
                    active: item.active,
                    to: item.to,
                    onClick: item.onClick,
                    hasChildren: !!item.children && item.children.length > 0,
                  }))
                : []),
              ...(section.type === "simple"
                ? (section.simpleItems || []).map((item, i) => ({
                    key: `simple-${i}`,
                    title: item.title,
                    icon: item.icon,
                    to: item.to,
                    onClick: item.onClick,
                    hasChildren: !!item.children && item.children.length > 0,
                  }))
                : []),
            ];

            if (items.length === 0) return null;

            return (
              <SidebarSectionWrapper
                key={sectionIndex}
                sx={{ alignItems: "center" }}
              >
                <SidebarSectionLabel
                  sx={{ textAlign: "center", padding: "4px 0 6px" }}
                >
                  <Typography
                    sx={{
                      fontSize: "9px",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "1px",
                      color: theme.palette.text.tertiary,
                      lineHeight: 1.2,
                    }}
                  >
                    {section.label}
                  </Typography>
                </SidebarSectionLabel>

                {items.map((item) => (
                  <Tooltip key={item.key} title={item.title} placement="right">
                    <CollapsedIconTile
                      active={item.active}
                      onClick={() => handleItemClick(item)}
                    >
                      {item.icon}
                    </CollapsedIconTile>
                  </Tooltip>
                ))}
              </SidebarSectionWrapper>
            );
          })}
        </SidebarBody>
      </SidebarContainer>
    );
  }

  return (
    <SidebarContainer width={resolvedWidth}>
      {/* Header: Corporate + Initiative */}
      <SidebarHeader>
        <Box>
          <Typography
            sx={{
              fontSize: "10px",
              fontWeight: 600,
              color: theme.palette.text.tertiary,
              textTransform: "uppercase",
              letterSpacing: "1px",
              marginBottom: "4px",
            }}
          >
            Corporate
          </Typography>
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 700,
              color: theme.palette.primary.main,
            }}
          >
            {header.corporateName}
          </Typography>
        </Box>

        {header.initiativeOptions && header.initiativeOptions.length > 0 && (
          <Box>
            <Typography
              sx={{
                fontSize: "10px",
                fontWeight: 600,
                color: theme.palette.text.tertiary,
                textTransform: "uppercase",
                letterSpacing: "1px",
                marginBottom: "6px",
              }}
            >
              {header.initiativeLabel || "Iniziativa"}
            </Typography>
            <Select
              value={header.selectedInitiative || ""}
              onChange={(e) =>
                header.onInitiativeChange?.(e.target.value as string)
              }
              size="small"
              fullWidth
              displayEmpty
              startAdornment={
                <SlidersHorizontal
                  size={16}
                  color={theme.palette.text.tertiary}
                  style={{ marginRight: "8px", flexShrink: 0 }}
                />
              }
              sx={{
                borderRadius: "10px",
                fontSize: "13px",
                backgroundColor: theme.palette.action.hover,
                border: `1px solid ${theme.palette.divider}`,
                "& .MuiSelect-select": {
                  padding: "8px 12px 8px 0",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
                "& .MuiSelect-icon": {
                  color: theme.palette.text.tertiary,
                  right: "10px",
                },
              }}
            >
              {header.initiativeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </Box>
        )}
      </SidebarHeader>

      {/* Sections */}
      <SidebarBody>
        {sections.map((section, sectionIndex) => (
          <SidebarSectionWrapper key={sectionIndex}>
            <SidebarSectionLabel>
              <Typography
                sx={{
                  fontSize: "10px",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                  color: theme.palette.text.tertiary,
                }}
              >
                {section.label}
              </Typography>
            </SidebarSectionLabel>

            {section.type === "cards" &&
              section.items.map((item, itemIndex) => (
                <SidebarMenuCardCst key={itemIndex} {...item} />
              ))}

            {section.type === "simple" &&
              section.simpleItems?.map((item, itemIndex) => (
                <SidebarSimpleItem key={itemIndex} {...item} />
              ))}
          </SidebarSectionWrapper>
        ))}
      </SidebarBody>
    </SidebarContainer>
  );
};

export default SidebarCst;
